'use client';

import { useSearchParams } from 'next/navigation';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { unlockAccount } from '@/api/auth';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { useLogin } from '@/hooks/login';
import { useNavigation } from '@/hooks/navigation';
import { useSwrMutation } from '@/hooks/swr';
import { useCommonStore, useModalsStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { Spinner } from '@/styles/common';

export const UnlockAccount: FC = () => {
  const { t } = useTranslation('common');
  const { blackColor, mainColor } = useTheme();

  const searchParams = useSearchParams();
  const { navigate } = useNavigation();
  const open = useModalsStore.use.open();

  const { isAuthenticated, pendingUser, user } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      user: state.user,
    }))
  );

  const { resetRequestCount: resetRequestCountLogin } = useLogin();

  const {
    trigger: requestUnlockAccount,
    data: unlockAccountData,
    error: unlockAccountError,
    isMutating,
  } = useSwrMutation({
    key: 'unlockAccount',
    fetcher: unlockAccount,
  });

  useEffect(() => {
    if (pendingUser) {
      return;
    }

    if (user?.id != null) {
      navigate('/', true);
      return;
    }

    useCommonStore.setState({ afterLoginRedirectUrl: '/' });

    const unlockToken =
      searchParams.get('unlock_token') || searchParams.get('unlockToken');
    if (unlockToken) {
      if (isAuthenticated) {
        navigate('/', true);
      } else {
        requestUnlockAccount(unlockToken);
      }
    } else {
      open(Modals.Login);
    }
  }, [
    isAuthenticated,
    pendingUser,
    navigate,
    requestUnlockAccount,
    searchParams,
    user?.id,
    open,
  ]);

  useEffect(() => {
    if (unlockAccountData || unlockAccountError) {
      if (unlockAccountError) {
        showTooltip({
          type: TooltipTypes.ERROR,
          body: `${t('errorUnknown')}`,
        });
      } else {
        showTooltip({
          type: TooltipTypes.SUCCESS,
          body: `${t('auth.login.infoAccountUnlocked')}`,
        });
        navigate('/', true);
      }

      resetRequestCountLogin();
      open(Modals.Login);
    }
  }, [
    unlockAccountData,
    unlockAccountError,
    navigate,
    resetRequestCountLogin,
    t,
    open,
  ]);

  return isMutating ? (
    <Spinner
      size={60}
      margin="16px auto"
      color={mainColor}
      background={blackColor}
    />
  ) : null;
};
